import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Plus, Minus, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [variantLabel, setVariantLabel] = useState("");
  const [variantTouched, setVariantTouched] = useState(false);
  const [galleryImg, setGalleryImg] = useState(null);
  const { addToCart } = useApp();

  useEffect(() => {
    setNotFound(false);
    setProduct(null);
    setGalleryImg(null);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      const vs = r.data.variants || [];
      if (vs.length > 0) setVariantLabel(vs[0].label);
      // Canonicalise URL — if the URL slug differs from the DB slug
      // (case / hyphen mismatch), replace the address bar with the canonical
      // form so Google doesn't index the alternate spellings as duplicates.
      const canonicalSlug = r.data.slug;
      if (canonicalSlug && id && canonicalSlug !== id) {
        navigate(`/product/${canonicalSlug}`, { replace: true });
      }
    }).catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
      setProduct(null);
    });
  }, [id, navigate]);

  const selectedPrice = useMemo(() => {
    if (!product) return 0;
    const vs = product.variants || [];
    const match = vs.find((v) => v.label === variantLabel);
    return match ? match.price : product.price;
  }, [product, variantLabel]);

  const heroImage = useMemo(() => {
    if (!product) return "";
    // Manual thumbnail selection wins first — the shopper has explicitly
    // clicked a gallery image to inspect it.
    if (galleryImg) return galleryImg;
    const vi = product.variant_images || {};
    // Only swap to the variant-specific image once the shopper has actively
    // chosen a variant. On initial load we always show the default (whole
    // cake) image, even though a variant is auto-selected for pricing.
    if (variantTouched && variantLabel && vi[variantLabel]) return vi[variantLabel];
    return product.image;
  }, [product, variantLabel, variantTouched, galleryImg]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="product-not-found">
        <SEOHead
          title="Product not found — Cocoamaya Patisserie"
          description="This product is no longer available. Browse our full menu at Cocoamaya Patisserie."
          noindex={true}
        />
        <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>404</p>
        <h1 className="font-display text-4xl md:text-5xl italic mb-6">Product not found</h1>
        <p className="mb-8" style={{ color: "var(--ink-soft)" }}>
          The item you&apos;re looking for is no longer available. Browse our current menu of freshly baked cakes, pastries and breads.
        </p>
        <Link to="/shop" className="btn-primary" data-testid="not-found-shop-link">Back to shop</Link>
      </div>
    );
  }
  if (!product) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="product-loading">Loading…</div>;
  const variants = product.variants || [];

  // ----- SEO -----
  const canonical = `https://cocoamayapatisserie.com/product/${product.slug || product.id}`;
  const fullDesc = (product.description || `Order ${product.name} from Cocoamaya Patisserie — hand-crafted in Mumbai, delivered fresh.`)
    .replace(/\s+/g, " ")
    .trim();
  const priceStr = variants.length
    ? `From ₹${Math.round(Math.min(...variants.map(v => v.price)))}`
    : `₹${Math.round(product.price)}`;
  const pageTitle = product.seo_title || `${product.name} — ${priceStr} | Cocoamaya Patisserie`;
  const metaDesc = product.seo_description || fullDesc;
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    description: fullDesc,
    image: [product.image],
    brand: { "@type": "Brand", name: "Cocoamaya Patisserie" },
    category: product.category,
    offers: {
      "@type": "Offer",
      url: canonical,
      priceCurrency: "INR",
      price: Math.round(selectedPrice || product.price),
      availability: "https://schema.org/InStock",
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="product-detail">
      <SEOHead
        title={pageTitle}
        description={metaDesc}
        image={product.image}
        url={canonical}
        ogType="product"
        keywords={product.seo_keywords}
        jsonLd={jsonLd}
      />
      <Link to="/shop" className="eyebrow inline-flex items-center gap-2 mb-8" data-testid="back-to-shop">
        <ArrowLeft size={14} strokeWidth={1.5} /> Back to shop
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <div className="overflow-hidden rounded-2xl aspect-[4/5]" style={{ background: "var(--bg-card)" }}>
            <img src={heroImage} alt={product.name} className="w-full h-full object-cover" data-testid="product-hero-image" />
          </div>
          {(product.gallery && product.gallery.length > 0) && (
            <div className="mt-3 flex gap-3" data-testid="product-thumbnails">
              {[product.image, ...product.gallery].map((src, idx) => {
                const isActive = heroImage === src;
                return (
                  <button
                    key={`${src}-${idx}`}
                    onClick={() => setGalleryImg(src)}
                    className="w-20 h-20 md:w-24 md:h-24 overflow-hidden rounded-lg border-2 transition-all"
                    style={{ borderColor: isActive ? "var(--accent)" : "transparent", opacity: isActive ? 1 : 0.7 }}
                    data-testid={`thumb-${idx}`}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <img src={src} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="md:col-span-5 flex flex-col justify-center">
          <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>{product.category}</p>
          <h1 className="font-display text-5xl tracking-tight leading-none italic">{product.name}</h1>
          {/* Hidden local-SEO signal — crawlable by search engines,
              invisible to users. */}
          <p className="sr-only" data-testid="product-location-tagline">
            {product.name} in Andheri East, Mumbai
          </p>
          {product.category === "cakes" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {product.contains_egg ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{ background: "#FDECEC", color: "#B23B3B", border: "1px solid #E5A5A5" }}
                  data-testid="badge-contains-egg"
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: "#B23B3B" }} /> Contains Egg
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{ background: "#EAF3E3", color: "#2E6B29", border: "1px solid #A6C7A0" }}
                  data-testid="badge-veg"
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: "#2E6B29" }} /> 100% Vegetarian
                </span>
              )}
            </div>
          )}
          <p className="font-display text-3xl mt-6">₹{Math.round(selectedPrice)}</p>
          <p className="mt-6 text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--ink-soft)" }}>{product.description}</p>

          {variants.length > 0 && (
            <div className="mt-8">
              <p className="eyebrow mb-3">{variants.length === 1 ? "Size" : "Choose size"}</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.label}
                    onClick={() => { setVariantLabel(v.label); setVariantTouched(true); }}
                    className="px-5 py-3 border rounded-full text-sm transition-colors"
                    style={{
                      borderColor: variantLabel === v.label ? "var(--accent)" : "var(--line)",
                      background: variantLabel === v.label ? "var(--accent)" : "transparent",
                      color: variantLabel === v.label ? "#FAF3EB" : "var(--ink)",
                    }}
                    data-testid={`variant-${v.label}`}
                  >
                    <span className="font-medium">{v.label}</span>
                    <span className="ml-2" style={{ opacity: 0.8 }}>₹{Math.round(v.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border rounded-full" style={{ borderColor: "var(--ink)" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-12 flex items-center justify-center" data-testid="qty-decrease" aria-label="Decrease">
                <Minus size={14} strokeWidth={1.5} />
              </button>
              <span className="w-10 text-center" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-10 h-12 flex items-center justify-center" data-testid="qty-increase" aria-label="Increase">
                <Plus size={14} strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={() => addToCart(product, qty, variantLabel, selectedPrice)}
              className="btn-primary flex-1 justify-center"
              data-testid="detail-add-to-cart"
            >
              Add to cart · ₹{Math.round(selectedPrice * qty)}
            </button>
          </div>

          <div className="mt-12 pt-8 border-t text-sm leading-relaxed space-y-2" style={{ borderColor: "var(--line)", color: "var(--ink-soft)" }}>
            <p>Baked fresh the day of delivery.</p>
            <p>Same-day delivery available within Mumbai via Borzo courier.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
